// Thin service layer over the in-memory DB. UI never touches storage directly;
// it goes through these query helpers + the mutation actions exposed by AppContext.
// A future backend replaces the body of these functions with API calls.

export const query = {
  matters: (db) => db.matters,
  matter: (db, id) => db.matters.find((m) => m.id === id),
  clients: (db) => db.clients,
  client: (db, id) => db.clients.find((c) => c.id === id),
  user: (db, id) => db.users.find((u) => u.id === id),
  userName: (db, id) => db.users.find((u) => u.id === id)?.name || "—",
  clientName: (db, id) => db.clients.find((c) => c.id === id)?.name || "—",
  partiesFor: (db, mid) => db.parties.filter((p) => p.matterId === mid),
  hearingsFor: (db, mid) => db.hearings.filter((h) => h.matterId === mid),
  tasksFor: (db, mid) => db.tasks.filter((t) => t.matterId === mid),
  docsFor: (db, mid) => db.documents.filter((d) => d.matterId === mid),
  ordersFor: (db, mid) => db.orders.filter((o) => o.matterId === mid),
  billingFor: (db, mid) => db.billing.filter((b) => b.matterId === mid),
  notesFor: (db, mid) => db.notes.filter((n) => n.matterId === mid),
  commsFor: (db, mid) => db.communications.filter((c) => c.matterId === mid),
  draftsFor: (db, mid) => db.drafts.filter((d) => d.matterId === mid),
  mattersForClient: (db, cid) => db.matters.filter((m) => m.clientId === cid),
  billingForClient: (db, cid) => db.billing.filter((b) => b.clientId === cid),
};

// Global search across mock data — models a future server-side search response.
export const globalSearch = (db, term) => {
  const q = (term || "").trim().toLowerCase();
  if (!q) return [];
  const results = [];
  db.matters.forEach((m) => {
    if (
      m.title.toLowerCase().includes(q) ||
      m.caseNumber.toLowerCase().includes(q) ||
      m.ref.toLowerCase().includes(q)
    )
      results.push({ type: "Matter", id: m.id, label: m.title, sub: m.caseNumber, to: `/matters/${m.id}` });
  });
  db.clients.forEach((c) => {
    if (c.name.toLowerCase().includes(q))
      results.push({ type: "Client", id: c.id, label: c.name, sub: c.type, to: `/clients/${c.id}` });
  });
  db.documents.forEach((d) => {
    if (d.name.toLowerCase().includes(q))
      results.push({ type: "Document", id: d.id, label: d.name, sub: d.category, to: `/matters/${d.matterId}` });
  });
  db.drafts.forEach((d) => {
    if (d.title.toLowerCase().includes(q))
      results.push({ type: "Draft", id: d.id, label: d.title, sub: d.type, to: `/drafting/${d.id}` });
  });
  db.hearings.forEach((h) => {
    if (h.purpose.toLowerCase().includes(q) || q === "tomorrow hearings")
      results.push({ type: "Hearing", id: h.id, label: h.purpose, sub: h.court, to: `/matters/${h.matterId}` });
  });
  db.research.forEach((r) => {
    if (r.title.toLowerCase().includes(q) || r.proposition.toLowerCase().includes(q))
      results.push({ type: "Research", id: r.id, label: r.title, sub: r.citation, to: `/research` });
  });
  return results.slice(0, 20);
};

// Simulated async API latency for realism.
export const withLatency = (fn, ms = 500) =>
  new Promise((resolve) => setTimeout(() => resolve(fn()), ms));
